import { Trash2, X } from "lucide-react";
import * as React from "react";

import {
  type FieldOverrideMeta,
  type Layer,
  overridesApi,
} from "@/api/overrides";
import { LayerIndicator } from "@/components/form-builder/LayerIndicator";
import { LockLevelBadge } from "@/components/form-builder/LockLevelBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUIStore } from "@/stores/ui";

interface Props {
  open: boolean;
  formCode: string;
  fieldName: string;
  fieldLockLevel?: string;
  defaultLayer?: Exclude<Layer, "personal">;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}

const LAYER_OPTIONS: Array<{ value: Exclude<Layer, "personal">; label: string }> = [
  { value: "platform", label: "Platform (system)" },
  { value: "tenant", label: "Tenant (toàn tổ chức)" },
  { value: "role", label: "Role (theo vai trò)" },
];

const COMMON_KEYS = [
  "label",
  "placeholder",
  "help_text",
  "required",
  "readonly",
  "hidden",
  "default",
  "order",
];

/**
 * FieldOverrideDialog — admin chỉnh override per layer.
 *
 * Lightweight modal (không dùng Radix Dialog). Hỗ trợ:
 * - Chọn layer (platform/tenant/role) + layer_id (cho role).
 * - Edit override JSON (raw key=value table).
 * - Xem list override hiện có cho field.
 * - Delete từng override.
 */
export function FieldOverrideDialog({
  open,
  formCode,
  fieldName,
  fieldLockLevel,
  defaultLayer,
  onClose,
  onChanged,
}: Props): JSX.Element | null {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<FieldOverrideMeta[]>([]);
  const [layer, setLayer] = React.useState<Exclude<Layer, "personal">>(
    defaultLayer ?? "tenant",
  );
  const [layerId, setLayerId] = React.useState("");
  const [rows, setRows] = React.useState<Array<{ key: string; value: string }>>([
    { key: "", value: "" },
  ]);
  const [busy, setBusy] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const list = await overridesApi.listFieldOverrides(formCode, fieldName);
      setItems(list);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi load override",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  }, [formCode, fieldName, pushToast]);

  React.useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  if (!open) return null;

  const handleSave = async (): Promise<void> => {
    const overrides: Record<string, unknown> = {};
    for (const r of rows) {
      const k = r.key.trim();
      if (!k) continue;
      const v = r.value.trim();
      if (v === "true") overrides[k] = true;
      else if (v === "false") overrides[k] = false;
      else if (v === "null") overrides[k] = null;
      else if (/^-?\d+(\.\d+)?$/.test(v)) overrides[k] = Number(v);
      else if (v.startsWith("{") || v.startsWith("[")) {
        try {
          overrides[k] = JSON.parse(v);
        } catch {
          overrides[k] = v;
        }
      } else overrides[k] = v;
    }
    if (Object.keys(overrides).length === 0) {
      pushToast({ variant: "warning", title: "Cần ít nhất 1 key" });
      return;
    }
    setBusy(true);
    try {
      const res = await overridesApi.upsertOverride(formCode, fieldName, {
        layer,
        layer_id: layer === "role" && layerId ? layerId : null,
        overrides,
      });
      pushToast({
        variant: "success",
        title: `Lưu OK (${res.applied_keys.length} key)`,
        description:
          res.rejected_keys.length > 0
            ? `Bị từ chối: ${res.rejected_keys.join(", ")}`
            : undefined,
      });
      await refresh();
      await onChanged();
      setRows([{ key: "", value: "" }]);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (it: FieldOverrideMeta): Promise<void> => {
    if (it.layer === "personal") {
      // Personal là FE-only ở /me — admin không xoá được
      return;
    }
    if (!confirm(`Xoá override layer ${it.layer}?`)) return;
    setBusy(true);
    try {
      await overridesApi.deleteOverride(
        formCode,
        fieldName,
        it.layer as Exclude<Layer, "personal">,
        it.layer_id ?? undefined,
      );
      pushToast({ variant: "success", title: "Đã xoá" });
      await refresh();
      await onChanged();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Xoá thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg border shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-background">
          <div>
            <h2 className="font-semibold">Override field</h2>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                {formCode} → {fieldName}
              </code>
              <LockLevelBadge level={fieldLockLevel} />
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="size-4" />
          </button>
        </header>

        <div className="p-4 space-y-6">
          {/* Existing overrides */}
          <section>
            <h3 className="text-sm font-semibold mb-2">Override hiện có</h3>
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                Chưa có override nào.
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((it) => (
                  <div
                    key={`${it.layer}-${it.layer_id ?? "-"}`}
                    className="rounded border bg-muted/30 p-2"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <LayerIndicator layer={it.layer} />
                      {it.layer_id ? (
                        <code className="text-[10px] text-muted-foreground">
                          {it.layer_id.slice(0, 8)}...
                        </code>
                      ) : null}
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        {new Date(it.updated_at).toLocaleString("vi-VN")}
                      </span>
                      {it.layer !== "personal" ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="size-6"
                          onClick={() => void handleDelete(it)}
                          disabled={busy}
                        >
                          <Trash2 className="size-3" />
                        </Button>
                      ) : null}
                    </div>
                    <pre className="text-[11px] overflow-x-auto">
                      {JSON.stringify(it.overrides, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* New override form */}
          <section className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-2">Thêm / cập nhật override</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium block mb-1">Layer</label>
                <select
                  value={layer}
                  onChange={(e) =>
                    setLayer(e.target.value as Exclude<Layer, "personal">)
                  }
                  className="w-full h-8 rounded-md border bg-background px-2 text-sm"
                >
                  {LAYER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              {layer === "role" ? (
                <div>
                  <label className="text-xs font-medium block mb-1">
                    Role ID (UUID)
                  </label>
                  <Input
                    value={layerId}
                    onChange={(e) => setLayerId(e.target.value)}
                    placeholder="uuid của role muốn target"
                    className="h-8 text-sm font-mono"
                  />
                </div>
              ) : null}
              <div>
                <label className="text-xs font-medium block mb-1">
                  Override key=value (mỗi dòng 1 key)
                </label>
                <div className="space-y-1">
                  {rows.map((r, i) => (
                    <div key={i} className="flex gap-1">
                      <Input
                        value={r.key}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRows(
                            rows.map((row, j) =>
                              j === i ? { ...row, key: v } : row,
                            ),
                          );
                        }}
                        placeholder="key (vd: label, hidden, default)"
                        className="h-8 text-sm flex-1"
                        list="override-keys"
                      />
                      <Input
                        value={r.value}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRows(
                            rows.map((row, j) =>
                              j === i ? { ...row, value: v } : row,
                            ),
                          );
                        }}
                        placeholder="value (true/false/số/string/JSON)"
                        className="h-8 text-sm flex-1"
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-7"
                        onClick={() =>
                          setRows(rows.filter((_, j) => j !== i))
                        }
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  ))}
                  <datalist id="override-keys">
                    {COMMON_KEYS.map((k) => (
                      <option key={k} value={k} />
                    ))}
                  </datalist>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRows([...rows, { key: "", value: "" }])}
                  >
                    + Thêm row
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Server enforce lock_level. Key bị reject sẽ hiển thị trong toast.
                Tham khảo các key cho phép tại <Badge variant="outline" className="text-[10px]">Doc 23 §2.1</Badge>.
              </p>
            </div>
          </section>
        </div>

        <footer className="px-4 py-3 border-t flex justify-end gap-2 sticky bottom-0 bg-background">
          <Button variant="outline" onClick={onClose}>
            Đóng
          </Button>
          <Button onClick={() => void handleSave()} disabled={busy}>
            {busy ? "Đang lưu..." : "Lưu override"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
